// Execution-context classifier for the risk gate (WS-E / CK-E mandatory #2).
// Distinguishes slices whose bodies are INERT at submission time (stored
// routine definitions — the destructive keywords only run when the routine is
// later invoked) from immediately-executed procedural blocks (PG `DO $$…$$`,
// Oracle anonymous `BEGIN`/`DECLARE` blocks) whose bodies MUST be inspected.
//
// ADDITIVE: `compoundBlocks.ts`'s `isRoutineBody` deliberately conflates the
// two (it only cares about `;`-suppression); this classifier lives in the
// risk-gate path and does not touch the scanner's semantics.

import type { SqlEngine } from '../sql-grammar/model';

export type ExecutionContext =
  | 'top-level'
  | 'stored-routine-definition'
  | 'immediate-procedural-block';

// Object kinds whose CREATE/ALTER carries a stored procedural body. Mirrors
// compoundBlocks' ROUTINE_KINDS plus the MSSQL PROC/FUNC shorthands the
// legacy stripRoutineBodies recognized.
const ROUTINE_KINDS: ReadonlySet<string> = new Set([
  'PROCEDURE', 'PROC', 'FUNCTION', 'FUNC', 'TRIGGER', 'EVENT', 'PACKAGE', 'TYPE',
]);

// Lead-word noise tolerated between CREATE/ALTER and the object kind.
// Superset of compoundBlocks' LEAD_NOISE (adds Oracle's EDITIONABLE pair).
const LEAD_NOISE: ReadonlySet<string> = new Set([
  'OR', 'ALTER', 'REPLACE', 'DEFINER', 'ALGORITHM', 'SQL', 'SECURITY',
  'NONCLUSTERED', 'EDITIONABLE', 'NONEDITIONABLE',
]);

const WORD = /[A-Za-z_][A-Za-z0-9_$#]*/g;

/**
 * Classify one statement slice's execution context.
 *
 * @param codeProjection The slice's CODE-ONLY projection (comments/literals
 *   blanked) — quoted identifiers like `` `root`@`localhost` `` in a MySQL
 *   DEFINER clause must not leak words into the lead-word scan.
 */
export function classifyExecutionContext(
  codeProjection: string,
  engine: SqlEngine,
): ExecutionContext {
  WORD.lastIndex = 0;
  const words: string[] = [];
  let m: RegExpExecArray | null;
  // The classification never needs more than a handful of lead words.
  while (words.length < 12 && (m = WORD.exec(codeProjection)) !== null) {
    words.push(m[0].toUpperCase());
  }
  if (words.length === 0) return 'top-level';

  const first = words[0];
  if (first === 'CREATE' || first === 'ALTER') {
    for (let i = 1; i < words.length; i++) {
      if (ROUTINE_KINDS.has(words[i])) return 'stored-routine-definition';
      if (!LEAD_NOISE.has(words[i])) return 'top-level';
    }
    return 'top-level';
  }
  // Oracle anonymous PL/SQL block — executes immediately.
  if (engine === 'oracle' && (first === 'BEGIN' || first === 'DECLARE')) {
    return 'immediate-procedural-block';
  }
  // PostgreSQL DO block — executes immediately.
  if (engine === 'postgres' && first === 'DO') {
    return 'immediate-procedural-block';
  }
  return 'top-level';
}
